// Package-shape helpers that sit ON TOP of domain.js — they read/patch the
// PERSISTED client fields (packageHistory, currentPackageId, counters), whereas
// domain.js is pure display + guards. Kept out of domain so `api/` never pulls
// in React.
import { useMemo, useCallback } from "react";
import { isSessionBased, packageDisplayState } from "../domain";
import { pkgId } from "./format";

// Active package from a client (falls back to legacy c.package).
export const getActivePkg = (c) => {
  if (c.packageHistory?.length && c.currentPackageId) {
    return c.packageHistory.find((p) => p.packageId === c.currentPackageId) || null;
  }
  if (c.package?.type && c.package.type !== "none") return { ...c.package, packageId: "legacy", status: "active" };
  return null;
};

// Display name for any package type.
export const pkgDisplayName = (pkg) => {
  if (!pkg?.type || pkg.type === "none") return "No Package";
  if (pkg.type === "template_sessions" || pkg.type === "template_duration") return pkg.templateName || "Template Package";
  if (pkg.type === "custom") return pkg.customName || "Custom Package";
  if (pkg.type === "half_yearly") return "Half Yearly";
  return pkg.type.charAt(0).toUpperCase() + pkg.type.slice(1);
};

// Migrate a legacy client (c.package, no packageHistory) to the new structure.
export const migrateLegacyClient = (c) => {
  if (c.packageHistory?.length || !c.package?.type || c.package.type === "none") return c;
  const id = pkgId();
  return {
    ...c,
    currentPackageId: id,
    packageHistory: [{ packageId: id, ...c.package, status: "active", totalAmount: c.packagePrice || c.package?.totalAmount || 0 }],
  };
};

// Counter patch for marking ONE session present. Guarded so a non-session
// package's classesRemaining is never touched. (Not in domain — persisted
// counters, not display.)
export const markCounterPatch = (x) => ({
  classesAttended: (Number(x.classesAttended) || 0) + 1,
  classesRemaining: isSessionBased(x) ? Math.max(0, (Number(x.classesRemaining) || 0) - 1) : x.classesRemaining,
});

// Sum of a client's payments up to and including `payment`, in the order they
// were recorded — the payments array is newest-first (prepended on add), so
// reverse before the stable date sort. Deliberately NOT client.paid (the live
// cache); receipts want the historical "as of this payment" figure.
export const paidSoFarAsOf = (payments, client, payment) => {
  const ordered = (payments || []).filter((x) => x.clientId === (client?.id || payment.clientId)).reverse()
    .sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  const i = ordered.findIndex((x) => x.id === payment.id);
  return ordered.slice(0, i < 0 ? ordered.length : i + 1).reduce((s, x) => s + (Number(x.amount) || 0), 0);
};

// One packageDisplayState per client, recomputed only when `clients` changes.
export const usePackageStates = (clients) => {
  const map = useMemo(() => new Map(clients.map((c) => [c.id, packageDisplayState(c)])), [clients]);
  return useCallback((c) => map.get(c.id) || packageDisplayState(c), [map]);
};
